import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { TabsContent } from "../ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import { Plus, Edit2, Trash2, Power, PowerOff, Layers } from "lucide-react";

/**
 * ConvergentTab — Pestaña "Paquetes Convergentes" del catálogo.
 *
 * Extraída 1:1 desde `pages/CatalogPage.js`. Lista los paquetes predefinidos (fibra + móvil)
 * con sus servicios componentes, precio total y estado; permite crear, editar, activar/
 * desactivar y eliminar. Muestra un empty-state cuando no hay paquetes configurados.
 *
 * @param {Array}    props.convergentPackages     Paquetes convergentes existentes.
 * @param {Function} props.openConvergentCreate   Abre el modal en modo creación.
 * @param {Function} props.openConvergentEdit     (pkg) => void — abre el modal en modo edición.
 * @param {Function} props.formatCurrency         Formateador de moneda (EUR) del padre.
 * @param {Function} props.handleToggleConvergent (pkg) => void — activa/desactiva el paquete.
 * @param {Function} props.canDelete              (resource) => boolean — permiso RBAC para eliminar.
 * @param {Function} props.handleDeleteConvergent (id) => void — elimina un paquete.
 */
export const ConvergentTab = ({
  convergentPackages,
  openConvergentCreate,
  openConvergentEdit,
  formatCurrency,
  handleToggleConvergent,
  canDelete,
  handleDeleteConvergent,
}) => {
  return (
        <TabsContent value="convergent" className="mt-6">
          <Card className="bg-[#0F0F3D] border-[#1E1E5F]">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white">Paquetes Convergentes</CardTitle>
              <Button
                onClick={openConvergentCreate}
                className="bg-[#42E695] text-[#04042C]"
                data-testid="create-convergent-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Nuevo Paquete
              </Button>
            </CardHeader>
            <CardContent>
              {convergentPackages.length === 0 ? (
                <div className="text-center py-8">
                  <Layers className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                  <p className="text-slate-400">No hay paquetes convergentes configurados</p>
                  <p className="text-xs text-slate-500 mt-1">Crea paquetes predefinidos que combinen fibra + móvil para que los comerciales los seleccionen fácilmente.</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow className="border-[#1E1E5F]">
                      <TableHead className="text-[#03BEE4]">Nombre</TableHead>
                      <TableHead className="text-[#03BEE4]">Servicios</TableHead>
                      <TableHead className="text-[#03BEE4]">Precio Total</TableHead>
                      <TableHead className="text-[#03BEE4]">Estado</TableHead>
                      <TableHead className="text-[#03BEE4]">Acciones</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {convergentPackages.map((pkg) => (
                      <TableRow key={pkg.id} className="border-[#1E1E5F] hover:bg-[#1B1B4B]">
                        <TableCell className="font-medium text-white">
                          <div>
                            {pkg.nombre}
                            {pkg.descripcion && <p className="text-xs text-slate-500 mt-0.5">{pkg.descripcion}</p>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-0.5">
                            {pkg.servicios.map((svc, idx) => (
                              <div key={idx} className="text-sm text-slate-300">
                                {svc.nombre}{svc.linea_numero ? ` (L${svc.linea_numero})` : ""} — <span className="text-[#42E695]">{formatCurrency(svc.precio)}</span>
                              </div>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell className="text-[#42E695] font-semibold">
                          {formatCurrency(pkg.precio_total)}
                        </TableCell>
                        <TableCell>
                          <Badge className={pkg.is_active ? "bg-[#42E695]/20 text-[#42E695]" : "bg-slate-500/20 text-slate-400"}>
                            {pkg.is_active ? "Activo" : "Inactivo"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="sm" onClick={() => openConvergentEdit(pkg)} data-testid={`edit-convergent-${pkg.id}`}>
                              <Edit2 className="h-4 w-4 text-[#03BEE4]" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleToggleConvergent(pkg)}>
                              {pkg.is_active ? <PowerOff className="h-4 w-4 text-orange-400" /> : <Power className="h-4 w-4 text-[#42E695]" />}
                            </Button>
                            {canDelete("catalog") && (
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteConvergent(pkg.id)} data-testid={`delete-convergent-${pkg.id}`}>
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
  );
};
